import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import moment from 'moment';
import {forkJoin, Observable, of} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {EventService} from '../../_services/event.service';
import {MonitorService} from '../../_services/monitor.service';
import {BetweenDates, DrivingEventHistory, Monitor, SlotHistoryRequest} from '../../models/eventRequest';
import {ActiveSlotEvent, buildMonitorTimelines, MonitorTimeline, MonitorTimelineEntry, SlotOccupant, slotKey} from './monitor-timeline.builder';

/** Un champ modifié : {@code from} seul = valeur supprimée, {@code to} seul = valeur créée. */
interface Change {
  label: string;
  icon: string;
  from?: string;
  to?: string;
}

interface HistoryRow {
  history: DrivingEventHistory;
  changes: Change[];
  /** Mouvement par rapport au créneau recherché (arrivée / départ), null sinon. */
  slotMove: 'in' | 'out' | null;
}

type GroupStatus = 'here' | 'moved' | 'deleted';

interface HistoryGroup {
  eventId: number;
  rows: HistoryRow[];
  status: GroupStatus;
  slot: string;
  monitor: string;
  student: string;
}

const ACTIONS: Record<string, { label: string, icon: string }> = {
  CREATE: {label: 'Création', icon: 'fa-plus'},
  UPDATE: {label: 'Modification', icon: 'fa-pencil'},
  DELETE: {label: 'Suppression', icon: 'fa-trash'}
};

const STATUSES: Record<GroupStatus, { label: string, icon: string, order: number }> = {
  here: {label: 'Sur ce créneau', icon: 'fa-check-circle', order: 0},
  moved: {label: 'Déplacée', icon: 'fa-share', order: 1},
  deleted: {label: 'Supprimée', icon: 'fa-times-circle', order: 2}
};

@Component({
    selector: 'app-event-history',
    templateUrl: './event-history.component.html',
    styleUrls: ['./event-history.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EventHistoryComponent implements OnInit {

  readonly statuses = STATUSES;
  readonly roleIcons: Record<string, string> = {
    created: 'fa-plus', arrived: 'fa-sign-in', stay: 'fa-dot-circle-o', left: 'fa-sign-out', deleted: 'fa-trash'
  };

  mode: 'slot' | 'period' = 'slot';
  /** Affichage des résultats par créneau : par heure (historique) ou chronologie par moniteur. */
  viewMode: 'event' | 'monitor' = 'event';
  monitors: Monitor[] = [];

  slotDate = moment().format('YYYY-MM-DD');
  slotHour = '09:00';
  slotMonitorId: number | null = null;

  periodStart = moment().format('YYYY-MM-DD');
  periodEnd = moment().format('YYYY-MM-DD');

  /** Créneau et moniteur effectivement recherchés (figés au moment de la recherche). */
  searchedSlot = '';
  searchedMonitor = '';

  groups: HistoryGroup[] = [];
  rows: HistoryRow[] = [];
  monitorTimelines: MonitorTimeline[] = [];
  collapsedMonitors = new Set<number>();
  private changesCache = new Map<number, Change[]>();
  searched = false;
  loading = false;
  messageError = '';

  constructor(private eventService: EventService, private monitorService: MonitorService) { }

  ngOnInit(): void {
    this.monitorService.getAllMonitors().subscribe(data => this.monitors = data);
  }

  onChangeMode(mode: 'slot' | 'period') {
    this.mode = mode;
    this.reset();
  }

  onSearchSlot() {
    if (!this.slotDate || !this.slotHour) {
      this.messageError = 'Merci de définir une date et une heure !';
      return;
    }
    // Même convention que la modale d'édition : l'heure saisie est envoyée en UTC
    const startAt = moment.utc(this.slotDate + 'T' + this.slotHour);
    const slot = new SlotHistoryRequest(startAt, this.slotMonitorId);
    const monitor = this.monitors.find(m => m.id === this.slotMonitorId);
    const monitorFilter = this.slotMonitorId;
    const day = moment(this.slotDate).toDate();
    const request = forkJoin({
      history: this.eventService.getHistoryBySlot(slot),
      // Heures actives réelles : vérifie la dernière affectation (null si indisponible)
      active: this.eventService.getEventsBetweenDates(new BetweenDates(day, day), null as any).pipe(catchError(() => of(null)))
    });
    this.search(request, ({history, active}) => {
      this.searchedSlot = this.formatSlot(startAt.toISOString());
      this.searchedMonitor = monitor ? `${monitor.firstname} ${monitor.lastname}` : 'Tous les moniteurs';
      this.groups = this.buildGroups(history);
      const key = slotKey(startAt.toISOString());
      const activeEvents = active == null ? null : this.toActiveEvents(active, key);
      this.monitorTimelines = buildMonitorTimelines(history, key, monitorFilter, this.monitorNames(), activeEvents);
    });
  }

  onSearchPeriod() {
    if (!this.periodStart || !this.periodEnd) {
      this.messageError = 'Merci de définir une période !';
      return;
    }
    const dates = new BetweenDates(moment(this.periodStart).toDate(), moment(this.periodEnd).toDate());
    this.search(this.eventService.getHistoryBetweenDates(dates), (data: DrivingEventHistory[]) => {
      this.searchedSlot = '';
      this.rows = data.map(h => this.toRow(h));
    });
  }

  // ----- Chronologie par moniteur -----

  isCollapsed(t: MonitorTimeline): boolean {
    return this.collapsedMonitors.has(t.monitorId);
  }

  toggleMonitor(t: MonitorTimeline) {
    if (this.isCollapsed(t)) {
      this.collapsedMonitors.delete(t.monitorId);
    } else {
      this.collapsedMonitors.add(t.monitorId);
    }
  }

  setAllCollapsed(collapsed: boolean) {
    this.collapsedMonitors = new Set(collapsed ? this.monitorTimelines.map(t => t.monitorId) : []);
  }

  /** Dernière affectation : l'état réel s'il a pu être vérifié, sinon celui reconstruit par l'audit. */
  currentOccupants(t: MonitorTimeline): SlotOccupant[] {
    return t.actualOccupants ?? t.replayedOccupants;
  }

  /** Nombre total d'heures actuellement sur le créneau, tous moniteurs affichés confondus. */
  slotHourCount(): number {
    return this.monitorTimelines.reduce((sum, t) => sum + this.currentOccupants(t).length, 0);
  }

  /**
   * Statut de la carte :
   * - created : l'heure actuelle a été créée directement sur le créneau
   * - arrived : l'heure actuelle est arrivée par un changement de créneau ou de moniteur
   * - deleted / left : créneau libéré par une suppression / un départ
   * - unknown : heure actuelle sans historique, ou carte sans opération
   */
  cardStatus(t: MonitorTimeline): 'created' | 'arrived' | 'deleted' | 'left' | 'unknown' {
    const occupant = this.currentOccupants(t)[0];
    if (occupant) {
      const arrival = [...t.entries].reverse().find(e => e.role === 'in' && e.history.eventId === occupant.eventId);
      return arrival ? (arrival.history.action === 'CREATE' ? 'created' : 'arrived') : 'unknown';
    }
    if (t.entries.length === 0) {
      return 'unknown';
    }
    return this.lastEntry(t).history.action === 'DELETE' ? 'deleted' : 'left';
  }

  /** Dernière opération de la carte : celle qui a libéré le créneau quand il est vide. */
  lastEntry(t: MonitorTimeline): MonitorTimelineEntry {
    return t.entries[t.entries.length - 1];
  }

  /** Variante visuelle du rôle : distingue création / arrivée par déplacement et suppression / départ. */
  roleClass(e: MonitorTimelineEntry): string {
    switch (e.role) {
      case 'in':
        return e.history.action === 'CREATE' ? 'created' : 'arrived';
      case 'out':
        return e.history.action === 'DELETE' ? 'deleted' : 'left';
      default:
        return 'stay';
    }
  }

  /** Heures présentes d'un seul côté (audit ou réel). */
  mismatchedEvents(t: MonitorTimeline): number[] {
    const replayed = t.replayedOccupants.map(o => o.eventId);
    const actual = (t.actualOccupants || []).map(o => o.eventId);
    return [...actual.filter(id => !replayed.includes(id)), ...replayed.filter(id => !actual.includes(id))];
  }

  entryActionLabel(h: DrivingEventHistory): string {
    return h.action === 'CREATE' && h.modifiedBy === 'system-init' ? 'Initialisation de l\'historique' : this.actionLabel(h);
  }

  roleLabel(e: MonitorTimelineEntry): string {
    switch (e.role) {
      case 'in':
        return e.history.action === 'CREATE' ? 'créée sur le créneau' : 'arrive sur le créneau';
      case 'out':
        return e.history.action === 'DELETE' ? 'supprimée du créneau' : 'quitte le créneau';
      default:
        return 'reste sur le créneau';
    }
  }

  /** Provenance (arrivée) ou destination (départ) d'une heure déplacée ou réaffectée. */
  roleContext(e: MonitorTimelineEntry): string {
    const h = e.history;
    if (h.action !== 'UPDATE' || e.role === 'stay') {
      return '';
    }
    const [at, monitor] = e.role === 'in' ? [h.oldStartAt, h.oldMonitorName] : [h.newStartAt, h.newMonitorName];
    return `${e.role === 'in' ? 'depuis' : 'vers'} le ${this.formatSlot(at)} · ${this.or(monitor)}`;
  }

  entryStudent(e: MonitorTimelineEntry): string {
    return this.or(e.role === 'out' ? e.history.oldStudentName : e.history.newStudentName);
  }

  changesOf(h: DrivingEventHistory): Change[] {
    if (!this.changesCache.has(h.id)) {
      this.changesCache.set(h.id, this.changes(h));
    }
    return this.changesCache.get(h.id) as Change[];
  }

  countByStatus(status: GroupStatus): number {
    return this.groups.filter(g => g.status === status).length;
  }

  actionLabel(h: DrivingEventHistory): string {
    return ACTIONS[h.action].label;
  }

  actionIcon(h: DrivingEventHistory): string {
    return ACTIONS[h.action].icon;
  }

  formatSlot(date: string): string {
    return date ? moment.utc(date).format('DD/MM/YYYY HH:mm') : '';
  }

  formatModifiedAt(date: string): string {
    return moment(date).format('DD/MM/YYYY à HH:mm:ss');
  }

  /** Créneau concerné : le nouveau s'il existe, sinon l'ancien (suppression). */
  slotOf(h: DrivingEventHistory): string {
    return this.formatSlot(h.newStartAt || h.oldStartAt);
  }

  private buildGroups(data: DrivingEventHistory[]): HistoryGroup[] {
    const byEvent = new Map<number, DrivingEventHistory[]>();
    data.forEach(h => byEvent.set(h.eventId, [...(byEvent.get(h.eventId) || []), h]));

    const groups: HistoryGroup[] = [];
    byEvent.forEach((histories, eventId) => {
      const last = histories[histories.length - 1];
      const deleted = last.action === 'DELETE';
      const slot = this.formatSlot(deleted ? last.oldStartAt : last.newStartAt);
      groups.push({
        eventId,
        rows: histories.map(h => this.toRow(h)),
        status: deleted ? 'deleted' : (slot === this.searchedSlot ? 'here' : 'moved'),
        slot,
        monitor: this.or(deleted ? last.oldMonitorName : last.newMonitorName),
        student: this.or(deleted ? last.oldStudentName : last.newStudentName)
      });
    });
    return groups.sort((a, b) => STATUSES[a.status].order - STATUSES[b.status].order || a.eventId - b.eventId);
  }

  private monitorNames(): Map<number, string> {
    return new Map(this.monitors.map(m => [m.id, `${m.firstname} ${m.lastname}`] as [number, string]));
  }

  private toActiveEvents(events: any[], key: string): ActiveSlotEvent[] {
    return events
      .filter(e => e.monitor && slotKey(e.startAt) === key)
      .map(e => ({
        eventId: e.id,
        monitorId: e.monitor.id,
        studentName: e.student ? `${e.student.firstname} ${e.student.lastname}`.trim() : null
      }));
  }

  private toRow(h: DrivingEventHistory): HistoryRow {
    return {history: h, changes: this.changes(h), slotMove: this.slotMove(h)};
  }

  private slotMove(h: DrivingEventHistory): 'in' | 'out' | null {
    if (!this.searchedSlot || h.action !== 'UPDATE') {
      return null;
    }
    const from = this.formatSlot(h.oldStartAt);
    const to = this.formatSlot(h.newStartAt);
    if (from === to) {
      return null;
    }
    return to === this.searchedSlot ? 'in' : (from === this.searchedSlot ? 'out' : null);
  }

  private changes(h: DrivingEventHistory): Change[] {
    if (h.action === 'CREATE') {
      return [
        {label: 'Créneau', icon: 'fa-clock-o', to: this.formatSlot(h.newStartAt)},
        {label: 'Moniteur', icon: 'fa-id-badge', to: this.or(h.newMonitorName)},
        {label: 'Élève', icon: 'fa-user', to: this.or(h.newStudentName)},
        {label: 'Boîte', icon: 'fa-car', to: this.or(h.newBoxType)}
      ];
    }
    if (h.action === 'DELETE') {
      return [
        {label: 'Créneau', icon: 'fa-clock-o', from: this.formatSlot(h.oldStartAt)},
        {label: 'Moniteur', icon: 'fa-id-badge', from: this.or(h.oldMonitorName)},
        {label: 'Élève', icon: 'fa-user', from: this.or(h.oldStudentName)}
      ];
    }
    const result: Change[] = [];
    this.pushIfChanged(result, 'Créneau', 'fa-clock-o', this.formatSlot(h.oldStartAt), this.formatSlot(h.newStartAt));
    this.pushIfChanged(result, 'Moniteur', 'fa-id-badge', h.oldMonitorName, h.newMonitorName, h.oldMonitorId, h.newMonitorId);
    this.pushIfChanged(result, 'Élève', 'fa-user', h.oldStudentName, h.newStudentName, h.oldStudentId, h.newStudentId);
    this.pushIfChanged(result, 'Boîte', 'fa-car', h.oldBoxType, h.newBoxType);
    return result;
  }

  private pushIfChanged(result: Change[], label: string, icon: string, oldLabel: string, newLabel: string,
                        oldKey: any = oldLabel, newKey: any = newLabel) {
    if ((oldKey || null) !== (newKey || null)) {
      result.push({label, icon, from: this.or(oldLabel), to: this.or(newLabel)});
    }
  }

  private or(value: string): string {
    return value || 'aucun';
  }

  private search<T>(request: Observable<T>, onData: (data: T) => void) {
    this.reset();
    this.loading = true;
    request.subscribe(
      (data: T) => {
        onData(data);
        this.searched = true;
        this.loading = false;
      },
      (err: any) => {
        this.messageError = err.error?.message || 'Erreur lors de la recherche';
        this.loading = false;
      }
    );
  }

  private reset() {
    this.groups = [];
    this.rows = [];
    this.monitorTimelines = [];
    this.collapsedMonitors = new Set();
    this.changesCache.clear();
    this.searched = false;
    this.messageError = '';
  }
}
